import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import type { HourPoint } from "../sim/types";
import {
  calculateTimingAnalysis,
  calculateHourlyProfile,
  classifyTimingScore,
} from "../sim/usage-timing";

interface UsageTimingPanelProps {
  hours: HourPoint[];
  monthCount: number;
}

export default function UsageTimingPanel({
  hours,
  monthCount,
}: UsageTimingPanelProps) {
  const analysis = calculateTimingAnalysis(hours, monthCount);
  const profile = calculateHourlyProfile(hours);
  const classification = classifyTimingScore(analysis.score);

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 720;

  // Formatera kr/år för visning (positivt = besparing, negativt = förlust)
  const valueLabel =
    analysis.annualValueSek >= 0
      ? `+${Math.round(analysis.annualValueSek).toLocaleString("sv-SE")} kr/år`
      : `−${Math.round(Math.abs(analysis.annualValueSek)).toLocaleString("sv-SE")} kr/år`;

  // Data för grafen — staplar för förbrukning, linje för spotpris
  const chartData = profile.map((p) => ({
    hour: p.hour,
    hourLabel: `${p.hour.toString().padStart(2, "0")}:00`,
    förbrukning: Number(p.avgImportKWh.toFixed(2)),
    spotpris: Number(p.avgSpotPrice.toFixed(2)),
  }));

  return (
    <div className="usage-timing-panel">
      <div className="usage-timing-header">
        <div className={`usage-timing-score is-${classification.level}`}>
          <div className="usage-timing-score-value">
            {Math.round(analysis.score)}
            <span className="usage-timing-score-max">/100</span>
          </div>
          <div className="usage-timing-score-label">
            {classification.label}
          </div>
          <div className="usage-timing-score-scale">
            <span>0 sämst</span>
            <span>50 snitt</span>
            <span>100 bäst</span>
          </div>
        </div>
        <div className="usage-timing-summary">
          <div className="usage-timing-summary-item">
            <span className="usage-timing-summary-label">
              Värde av nuvarande timing
            </span>
            <strong
              className={
                analysis.annualValueSek >= 0
                  ? "usage-timing-value-positive"
                  : "usage-timing-value-negative"
              }
            >
              {valueLabel}
            </strong>
          </div>
          <div className="usage-timing-summary-item">
            <span className="usage-timing-summary-label">
              Ditt snitt-spotpris
            </span>
            <strong>
              {analysis.weightedAvgSpotPrice.toFixed(2)} kr/kWh
            </strong>
          </div>
          <div className="usage-timing-summary-item">
            <span className="usage-timing-summary-label">
              Periodens snittpris
            </span>
            <strong>
              {analysis.naiveAvgSpotPrice.toFixed(2)} kr/kWh
            </strong>
          </div>
        </div>
      </div>

      <p className="usage-timing-description">{classification.description}</p>

      <div className="usage-timing-chart-wrapper">
        <h4 className="usage-timing-chart-title">
          Förbrukningsmönster vs spotpris per klockslag
        </h4>
        <p className="usage-timing-chart-help">
          Staplarna visar genomsnittlig förbrukning per timme på dygnet.
          Linjen visar genomsnittligt spotpris. Om staplarna toppar där
          linjen är hög har du sämre timing. Där finns potentialen för
          batteriet att hjälpa.
        </p>
        <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              stroke="var(--rule)"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="hour"
              tick={{ fill: "var(--muted)", fontSize: isMobile ? 10 : 12 }}
              interval={isMobile ? 3 : 1}
              tickFormatter={(v) => `${v.toString().padStart(2, "0")}`}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: "var(--muted)", fontSize: isMobile ? 10 : 12 }}
              label={
                isMobile
                  ? undefined
                  : {
                      value: "kWh",
                      angle: -90,
                      position: "insideLeft",
                      fill: "var(--muted)",
                      style: { fontSize: 12 },
                    }
              }
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: "var(--muted)", fontSize: isMobile ? 10 : 12 }}
              label={
                isMobile
                  ? undefined
                  : {
                      value: "kr/kWh",
                      angle: 90,
                      position: "insideRight",
                      fill: "var(--muted)",
                      style: { fontSize: 12 },
                    }
              }
            />
            <Tooltip
              contentStyle={{
                background: "var(--bg)",
                border: "1px solid var(--rule)",
                borderRadius: "6px",
                fontSize: "0.85rem",
              }}
              labelFormatter={(v) =>
                `Kl ${v.toString().padStart(2, "0")}:00`
              }
              formatter={(value: number, name: string) => {
                if (name === "förbrukning")
                  return [`${value.toFixed(2)} kWh`, "Förbrukning"];
                if (name === "spotpris")
                  return [`${value.toFixed(2)} kr/kWh`, "Spotpris"];
                return [value, name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: "0.85rem", paddingTop: "0.5rem" }}
            />
            <Bar
              yAxisId="left"
              dataKey="förbrukning"
              fill="var(--accent)"
              opacity={0.7}
              name="Förbrukning"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="spotpris"
              stroke="#e89c2e"
              strokeWidth={2.5}
              dot={false}
              name="Spotpris"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
